import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  JOB_LISTING_DEFAULT_ITEMS_PER_PAGE,
  JOB_LISTING_ITEMS_PER_PAGE_OPTIONS,
} from "../../../../constants/app";
import { timesheetFilterOptions } from "../../../../models/vacancy";
import { useAppDispatch, useAppSelector } from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import { getCandidateJobListings, updateLocationPreferences } from "../../../../store/candidate/thunks";
import { getProfileDetailsThunk } from "../../../../store/onboarding/thunk";
import { TTimesheetSort } from "../../../../store/timesheet/types";
import PaginationControls from "../../../../utils/PaginationControls";
import SortFilterDropdown, { SortOrder } from "../../../UI/SortFilterDropdown";
import ".././Jobs.scss";
import CandidateJobHeader, { getIsStage3Activate } from "../CandidateJobHeader";
import CandidateVacancyCard from "../CandidateVacancyCard";
import ActivationAlert from "../Elements/ActivationAlert";
import ExpiringNotifications from "../Elements/ExpiringNotification";
import MyPreference from "../Elements/MyPreference";
import SortControls from "../Elements/SortControls";

/**
 * JobsListing Component
 *
 * This component is responsible for displaying a list of job listings for a candidate.
 * It includes features such as pagination, sorting, and dynamic updates based on user interactions.
 *
 * @component
 *
 * @dependencies
 * - `useAppDispatch`: Custom hook to dispatch Redux actions.
 * - `useAppSelector`: Custom hook to access Redux state.
 * - `useState`: React hook to manage local component state.
 * - `useEffect`: React hook to handle side effects.
 * - `useCallback`: React hook to memoize functions.
 *
 * @state
 * - `itemPerPageList` (number): Number of items to display per page.
 * - `currentPage` (number): Current page number for pagination.
 * - `totalPages` (number): Total number of pages available.
 * - `paginationLinks` (any[]): Links for pagination navigation.
 * - `sortBy` (string): Field by which the job listings are sorted.
 * - `sortOrder` (string): Order of sorting (e.g., ascending or descending).
 *
 * @redux
 * - `candidateJobsList`: Redux state containing the list of job listings for the candidate.
 *
 * @methods
 * - `getCandidateJobList`: Fetches the list of job listings for the candidate and updates the state.
 * - `handlePageChange`: Updates the current page based on the selected pagination link.
 * - `handlePageSizeChange`: Updates the number of items per page and resets to the first page.
 * - `handleTimeFrameSort`: Toggles the sorting order and updates the sorting field.
 *
 * @effects
 * - Fetches job listings when the component mounts or when `currentPage`, `itemPerPageList`, or `candidateJobsListData` changes.
 * - Updates pagination details when `candidateJobsListData` changes.
 *
 * @renders
 * - `ActivationAlert`: Displays an alert for account activation.
 * - `CandidateJobHeader`: Displays job statistics (e.g., total jobs, pending jobs, accepted jobs).
 * - `SortControls`: Provides sorting controls for job listings.
 * - `CandidateVacancyCard`: Displays individual job listing cards.
 * - `PaginationControls`: Provides pagination controls for navigating through job listings.
 *
 * @returns {JSX.Element} The rendered JobsListing component.
 */
const JobsListing: FC = () => {
  const dispatch = useAppDispatch();
  
  const verificationStatus = useAppSelector(
    (state) => state.onboarding.personaldetails?.verification_status
  );

  const reviewStatus = useAppSelector(
    (state) => state.onboarding.personaldetails?.review_status
  );

  const currentStage = useAppSelector(
    (state) => state.onboarding.personaldetails?.current_stage
  );

  const documentsExpired = useAppSelector(
    (state) => state.onboarding.userpersonaldetails?.is_documents_expired
  );
  const jobpreferences = useAppSelector(
    (state) => state.onboarding.jobpreferences
  );
  const candidateJobsList = useAppSelector(
    (state) => state.candidateJobs.candidateJobsList
  );

  const hasSimilarJobs = useAppSelector(
    (state) => state.candidateJobs.hasSimilarJobs
  );

  const similar_jobs_start_index = useAppSelector(
    (state) => state.candidateJobs.similar_jobs_start_index
  );

  const expired_docs_start_index = useAppSelector(
    (state) => state.candidateJobs.expired_docs_start_index
  );

  const profileSteps = useAppSelector(
    (state) => state.onboarding.candidateProfileSteps
  );
  const candidateJobsListData = candidateJobsList?.job_listing;

  const [itemPerPageList, setItemPerPageList] = useState<number>(
    JOB_LISTING_DEFAULT_ITEMS_PER_PAGE
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationLinks, setPaginationLinks] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>("timeframe");
  const [sortOrder, setSortOrder] = useState<string>("asc");
  const [selectDistanceAway, setSelectDistanceAway] = useState<number>(jobpreferences?.max_travel_distance ?? 10);
  const isFirstRender = useRef(true);

  const isStage3Activate = getIsStage3Activate(profileSteps);

  const jobs = candidateJobsList?.job_listing?.data ?? [];
  
  // Find the last index where is_documents_expired === 0
  const lastValidIndex = Array.isArray(jobs) ? jobs
    .map((job, index) => ({ job, index }))
    .filter(({ job }) => !job.is_documents_expired)
    .at(-1)?.index ?? -1 : undefined;

  const hasExpiredDocs = jobs.some(job => job.is_documents_expired);



  /**
   * Fetches the list of job listings for a candidate and updates the state.
   *
   * This function is wrapped in a `useCallback` hook to memoize it and prevent
   * unnecessary re-renders. It dispatches actions to show a transparent preloader
   * before fetching the data and hides it after the data is fetched.
   *
   * @async
   * @function
   * @returns {Promise<void>} A promise that resolves when the job listings are fetched and the state is updated.
   *
   * @dependencies
   * - `dispatch`: Redux dispatch function to trigger actions.
   * - `currentPage`: The current page number for pagination.
   * - `itemPerPageList`: The number of items to display per page.
   * - `sortOrder`: The order in which the job listings should be sorted (e.g., ascending or descending).
   * - `sortBy`: The field by which the job listings should be sorted.
   *
   * @dispatches
   * - `showTransparentPreloaderAction`: Action to show a transparent preloader.
   * - `getCandidateJobListings`: Action to fetch the candidate's job listings with the specified parameters.
   * - `hideTransparentPreloaderAction`: Action to hide the transparent preloader.
   */
  const getCandidateJobList = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());

    // Update location preferences first
    // await dispatch(updateLocationPreferences(selectDistanceAway));
    
    await dispatch(
      getCandidateJobListings({
        page: currentPage,
        per_page: itemPerPageList,
        sort_order: sortOrder,
        sort_by: sortBy,
        distance_away: selectDistanceAway
      })
    );
    await dispatch(getProfileDetailsThunk())
    dispatch(hideTransparentPreloaderAction());
  }, [dispatch, currentPage, itemPerPageList, sortBy, sortOrder]);

  /**
   * Fetches the candidate job listings when the component mounts or when the `currentPage` or `itemPerPageList` state changes.
   *
   * @effect
   * - Calls the `getCandidateJobList` function to fetch the job listings.
   */
  useEffect(() => {    
    getCandidateJobList();
  }, [currentPage, itemPerPageList, getCandidateJobList]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (candidateJobsListData) {
      setTotalPages(candidateJobsListData?.last_page || 1);
      setPaginationLinks(candidateJobsListData?.links);
    }
  }, [candidateJobsListData]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageSizeChange = (value: string | number) => {
    setItemPerPageList(Number(value));
    setCurrentPage(1); // Reset to the first page when changing items per page
  };

  /**
   * Handles the sorting of job listings based on the selected field and order.
   *
   * @param type - The field by which to sort the job listings (e.g., "timeframe" or "created_at").
   */
  const handleTimeFrameSort = (type: string) => {
    setSortBy(type);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleLocalSortChange = (field: string, order: SortOrder) => {
    timesheetFilterOptions.filter(option => {
      if (option.label === field) {
         handleTimeFrameSort(option.value as TTimesheetSort);
        return true;
      }
      return false;
    });
  };

  useEffect(() => {
    // Skip API call on first render (initial mount)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const updatePreferences = async () => {
      dispatch(showTransparentPreloaderAction());
      await dispatch(updateLocationPreferences(selectDistanceAway));
      await dispatch(
        getCandidateJobListings({
          page: currentPage,
          per_page: itemPerPageList,
          sort_order: sortOrder,
          sort_by: sortBy,
          distance_away: selectDistanceAway
        })
      );
      dispatch(hideTransparentPreloaderAction());
    }
    updatePreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDistanceAway]);

  return (
   
      <div className={`d-flex flex-column gap-20 ${ reviewStatus && reviewStatus === "approved" ? `gap-mobile-20` : `gap-mobile-1`}`}>
        {(reviewStatus !== "approved" || currentStage !== 3) && <ActivationAlert />}       
        { reviewStatus && reviewStatus === "approved" && currentStage === 3 ? 
          <>
            <ExpiringNotifications />
            {/* <div className="d-flex gap-3 justify-content-end align-items-center">
              <div className="fs-13 fw-400 text-black">Maximum distance away</div>
              <KmSelector 
                selectedDistancyAway={selectDistanceAway}
                setSelectedDistancyAway={setSelectDistanceAway}
              />
            </div> */}
          </> : ""
        }
        <CandidateJobHeader
          jobListingCount={candidateJobsList?.total_job_listing}
          pendingJobCount={candidateJobsList?.total_pending_jobs}
          acceptedJobCount={candidateJobsList?.total_accepted_jobs}
          isCandidateFilledAllForms={isStage3Activate}
          documentsExpired={documentsExpired}
        />
        {
          (reviewStatus === "approved" && currentStage === 3) && !documentsExpired &&
          <MyPreference 
            selectedDistancyAway={selectDistanceAway}
            setSelectedDistancyAway={setSelectDistanceAway}
          />
        }
        
        <div className="d-flex flex-column">
          {/** Mobile Sort */}
          <SortFilterDropdown
            sortFields={["Position Type", "Requested Timeframe", "Posted"]}
            defaultField="Posted"
            onChange={handleLocalSortChange}
            defaultSortOrder={sortOrder as SortOrder}
          />

          {/** Lissting header */}
          <SortControls
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleTimeFrameSort}
            page_type="jobs"
          />
          {/** Job listing row */}
          {candidateJobsList &&
          candidateJobsList?.job_listing?.data?.length > 0 ? (
            candidateJobsList?.job_listing?.data?.map((vacancy, index) => {
              // Show alert on the last valid job, or first job if all are invalid
              const isShowAlert = index === expired_docs_start_index;
              return (
                <CandidateVacancyCard
                  key={index}
                  vacancy={vacancy}
                  type="jobs"
                  getCandidateJobList={getCandidateJobList}
                  isCandidateFilledAllForms={isStage3Activate}
                  documentsExpired={documentsExpired}
                  isShowExpiringDocAlert={isShowAlert}
                  hasExpiredDocs={hasExpiredDocs}
                  lastValidIndex={lastValidIndex} 
                  similarJobIndex={index === similar_jobs_start_index && hasSimilarJobs}
                />
              );
            })
          ) : (
              <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
                <div className="fs-16 fw-400 text-grey-medium">
                  No Jobs Found
                </div>
              </div>
          )}
        </div>
        {/* Pagination Component */}
        {candidateJobsList &&
          candidateJobsList?.total_job_listing > JOB_LISTING_DEFAULT_ITEMS_PER_PAGE && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              paginationLinks={paginationLinks}
              itemsPerPage={itemPerPageList}
              itemsPerPageOptions={JOB_LISTING_ITEMS_PER_PAGE_OPTIONS}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handlePageSizeChange}
            />
          )}
        {/* Pagination Component */}
      </div>
  );
};

export default JobsListing;
